import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Search, RefreshCw, Target, Trash2, CreditCard as Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import GoalModal from './GoalModal';
import { Goal } from './types';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
  paused: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300',
  archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

const GoalsView: React.FC = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentGoal, setCurrentGoal] = useState<Goal | null>(null);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .order('status')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setGoals(data || []);
    } catch (error: any) {
      toast.error(`Error fetching goals: ${error.message}`);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoals();
  }, [user]);

  const filteredGoals = goals.filter((g) => {
    const q = searchQuery.toLowerCase();
    return g.name.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q);
  });

  const activeGoals = filteredGoals.filter((g) => g.status === 'active');
  const otherGoals = filteredGoals.filter((g) => g.status !== 'active');

  const totalAllocation = activeGoals.reduce((sum, g) => sum + (g.time_allocation || 0), 0);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this goal?')) return;
    try {
      const { error } = await supabase.from('goals').delete().eq('id', id);
      if (error) throw error;
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const GoalCard: React.FC<{ goal: Goal }> = ({ goal }) => (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => { setCurrentGoal(goal); setIsModalOpen(true); }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: goal.color }} />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{goal.name}</h3>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[goal.status] || STATUS_COLORS.archived}`}>
          {goal.status}
        </span>
      </div>

      {goal.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3 ml-6">
          {goal.description}
        </p>
      )}

      <div className="ml-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">Time target</span>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{goal.time_allocation}%</span>
        </div>
        <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${goal.time_allocation}%`, backgroundColor: goal.color }}
          />
        </div>
      </div>

      <div className="flex justify-end mt-3 gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); setCurrentGoal(goal); setIsModalOpen(true); }}
          className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => handleDelete(e, goal.id)}
          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Target className="w-8 h-8 mr-3 text-emerald-500" />
            Goals
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            The lens that makes everything else meaningful
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search goals..."
              className="pl-10 pr-4 py-2.5 w-56 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={() => { setCurrentGoal(null); setIsModalOpen(true); }}
            className="flex items-center justify-center p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            title="New Goal"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Allocation bar */}
      {activeGoals.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Time allocation across active goals</span>
            <span className={`text-sm font-semibold ${totalAllocation > 100 ? 'text-red-500' : totalAllocation === 100 ? 'text-emerald-500' : 'text-gray-500'}`}>
              {totalAllocation}% / 100%
            </span>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 gap-0.5">
            {activeGoals.filter(g => g.time_allocation > 0).map((goal) => (
              <div
                key={goal.id}
                title={`${goal.name}: ${goal.time_allocation}%`}
                className="h-full transition-all"
                style={{ width: `${goal.time_allocation}%`, backgroundColor: goal.color }}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {activeGoals.map((goal) => (
              <div key={goal.id} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: goal.color }} />
                <span className="text-xs text-gray-600 dark:text-gray-400">{goal.name} ({goal.time_allocation}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      ) : filteredGoals.length === 0 ? (
        <div className="text-center py-16">
          <Target className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No goals yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Define what you're working toward</p>
          <button
            onClick={() => { setCurrentGoal(null); setIsModalOpen(true); }}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Add your first goal
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {activeGoals.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Active</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeGoals.map((goal) => <GoalCard key={goal.id} goal={goal} />)}
              </div>
            </div>
          )}
          {otherGoals.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Other</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {otherGoals.map((goal) => <GoalCard key={goal.id} goal={goal} />)}
              </div>
            </div>
          )}
        </div>
      )}

      <GoalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        goal={currentGoal}
        onSuccess={fetchGoals}
      />
    </div>
  );
};

export default GoalsView;
